import * as yup from "yup";
import { validateDate } from "../datetime";
import { VALID_DATE, TIME_NOT_IN_FUTURE } from "@common/constants/validation";

export const ageVerificationSchema = ({
  hasChildrenInGamingArea,
  hasNoIdCustomerDescription,
}) =>
  yup.object().shape({
    date: VALID_DATE,
    time: TIME_NOT_IN_FUTURE,
    hasId: yup
      .boolean()
      .required("You must say if a customer has their ID or not."),
    idType: yup.object().when("hasId", {
      is: true,
      then: (schema) =>
        schema.shape({
          id: yup.number().positive().min(1, "ID type is required."),
          name: yup.string(),
          detailsRequired: yup.boolean(),
        }),
      otherwise: (schema) => schema.nullable(),
    }),
    otherId: yup.string().when(["hasId", "idType"], {
      is: (hasId, idType) =>
        hasId === true && idType && idType.detailsRequired === true,
      then: (schema) =>
        schema.required("If ID type is other, you must enter details."),
    }),
    gender: yup.object().when("hasId", {
      is: true,
      then: (schema) =>
        schema.shape({
          id: yup.number().positive().min(1, "Gender is required."),
          name: yup.string(),
          detailsRequired: yup.boolean(),
        }),
      otherwise: (schema) => schema.nullable(),
    }),
    dateOfBirth: yup.string().when("hasId", {
      is: true,
      then: (schema) =>
        schema
          .required("Date of birth is required.")
          .test("validDate", "Date is not valid.", validateDate),
    }),
    under18: yup.boolean(),
    under18Confirm: yup.boolean().when(["hasId", "under18"], {
      is: (hasId, under18) => hasId === true && under18 === true,
      then: (schema) =>
        schema.oneOf(
          [true],
          "You must confirm the customer is under 18 or re-enter their date of birth.",
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    confirmation: yup.boolean().when(["hasId", "under18"], {
      is: (hasId, under18) => hasId === true && under18 === false,
      then: (schema) => schema.oneOf([true], "Confirmation is required."),
      otherwise: (schema) => schema.nullable(),
    }),
    hasGambled: yup.boolean().when("hasId", {
      is: false,
      then: (schema) =>
        schema.required(
          "You must say if the customer gambled before the challenge.",
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    hasHadStakeReturned: yup.boolean().when(["hasId", "hasGambled"], {
      is: (hasId, hasGambled) => hasId === false && hasGambled === true,
      then: (schema) =>
        schema.oneOf(
          [true],
          "You must confirm the customer's stake has been returned",
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    hasBeenRefusedEntry: yup.boolean().when("hasId", {
      is: false,
      then: (schema) =>
        schema.oneOf(
          [true],
          "You must confirm the customer was refused entry.",
        ),
      otherwise: (schema) => schema.nullable(),
    }),
    comments: yup
      .string()
      .notRequired()
      .max(750, "Comments cannot exceed 750 characters."),

    // Children in gaming area validation - only when hasId is false
    childrenInArea: yup.boolean().when(["hasId"], {
      is: (hasId) => hasId === false && hasChildrenInGamingArea,
      then: (schema) =>
        schema.required(
          "You must indicate if there are children in the gaming area.",
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    requestedRemoveChild: yup.boolean().when(["hasId", "childrenInArea"], {
      is: (hasId, childrenInArea) =>
        hasId === false && hasChildrenInGamingArea && childrenInArea === true,
      then: (schema) =>
        schema.required(
          "You must indicate if you requested to remove the child.",
        ),
      otherwise: (schema) => schema.notRequired(),
    }),

    reasonNotRemoveChild: yup
      .string()
      .when(["hasId", "childrenInArea", "requestedRemoveChild"], {
        is: (hasId, childrenInArea, requestedRemoveChild) =>
          hasId === false &&
          hasChildrenInGamingArea &&
          childrenInArea === true &&
          requestedRemoveChild === false,
        then: (schema) =>
          schema
            .required("You must provide a reason for not removing the child.")
            .max(750, "Reason cannot exceed 750 characters."),
        otherwise: (schema) => schema.notRequired(),
      }),

    // Customer description validation
    customerDescription: yup.string().when(["hasId"], {
      is: (hasId) => hasId === false && hasNoIdCustomerDescription,
      then: (schema) =>
        schema
          .required("Customer description is required.")
          .max(750, "Customer description cannot exceed 750 characters."),
      otherwise: (schema) => schema.notRequired(),
    }),
  });
